import getBaseUrl from '../api/baseUrl';
import createBrowserHistory from 'history/createBrowserHistory';


const debug = !(window.thisEnvironmentType === "PROD");
const baseUrl = getBaseUrl(debug);

/**
 * Tries to figure out the appropriate URL to request for a service
 * If a URL starts with http, https or // we will assume it is an external URL
 */
function getURLToFetch(url){
    let externalURLRegex = /^http|^https|^\/\//;
    let urlToFetch;

    // Do not edit external URLs
    if(externalURLRegex.test(url)){
        urlToFetch = url;
    } else {
        urlToFetch = baseUrl + url;
    }

    return urlToFetch;
}

/**
 * Initiate a GET request
 * @param {string} url
 * @returns {Promise<Response>}
 */
function get(url){
    const urlToFetch = getURLToFetch(url);
    return fetch(urlToFetch, {
        credentials: "include", // This is necessary to get and send cookies with every request
    }).then(onSuccess, onError);
}

/**
 * Initiate a POST request
 * @param {string} url
 * @param {Object} data
 */
function post(url, data){
    const urlToFetch = getURLToFetch(url);
    const fetchConfig = {
        method: 'POST',
        credentials: "include", // This is necessary to get and send cookies with every request
        headers: {
            'Content-Type': 'application/json'
        }
    };
    if(data){
        fetchConfig.body = JSON.stringify(data)
    }
    return fetch(urlToFetch, fetchConfig).then(onSuccess, onError);
}

/**
 * Initiate a PUT request
 * @param {string} url
 * @param {Object} data
 */
function put(url, data){
    const urlToFetch = getURLToFetch(url);
    const fetchConfig = {
        method: 'PUT',
        credentials: "include", // This is necessary to get and send cookies with every request
        headers: {
            'Content-Type': 'application/json'
        }
    };
    if(data){
        fetchConfig.body = JSON.stringify(data);
    }
    return fetch(urlToFetch, fetchConfig).then(onSuccess, onError);
}

/**
 * First step to the response here - actual response is in the next then (), response handler defined elsewhere
 * @param response
 */
function onSuccess(response){
    //use fetch's ok flag to handle non successful HTTP response codes
    if (response.ok) {
        return response.json();
    } else {
        return response.json().then(result => {
            let errors = result.errors;
            if (errors) {
                return Promise.reject(errors);
            } else {
                return Promise.reject(response);
            }
        }).catch(errors => {
            return Promise.reject(getErrorText(errors));
        });
    }
}

/**
 * Primary error handler
 * using fetch this is only reached if offline, etc.
 * @param error
 */
function onError(error){
    console.log("Error printed in service.js", error);
}

/**
 * Get the error text from a variety of different error objects
 * @param errorObj
 */
function getErrorText(errorObj){
    const prettyMessage = getPrettyErrorMessage(errorObj);
    const {code} = errorObj;
    let errorMessage = "";
    if(Array.isArray(errorObj)){
        errorObj.forEach(err => {
            errorMessage += getErrorText(err) + "\n";
        });
    } else if (code) {
        errorMessage = getErrorMessageFromCode(code)
    } else if (prettyMessage) {
        errorMessage = prettyMessage;
    } else {
        // errorMessage = errorObj.toLocaleString();
        errorMessage = getErrorMessageFromCode();
    }
    return errorMessage;
}

/**
 * Returns a pretty error message if it exists in the response
 * @param [Object] errorObj
 * @returns {boolean}
 */
function getPrettyErrorMessage(errorObj){
    const possibleMessageKeys = ["responseText", "errorText", "errorMsg", "errorMessage"];
    const hasValue = possibleMessageKeys.some(key => {
        const value = possibleMessageKeys[key];
        return !!value;
    });
    return hasValue;
}

/**
 * Returns the appropriate error message string for an error code
 * @param code
 */
function getErrorMessageFromCode(code){
    let errorMessage;
    switch(code){
        case "cl05.article.expired":
            errorMessage = "The content you are trying to access is no longer available. If you have any questions, please contact your PublicRelay analyst.";
            break;
        case "cl05.bad.parameter":
        case "cl05.no.parameters":
        default:
            errorMessage = "The content you are trying to access is not currently available. If you have any questions, please contact your PublicRelay analyst.";
    }
    return errorMessage;
}

/**
 * Import all of service
 */
export default {
    get: get,
    post: post,
    put: put
};
